import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { listBuildings } from "@/api/buildings";
import { listEmergencies, sendEmergency, type EmergencyDraft, type EmergencyOutcome } from "@/api/preferences";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const FIELD = "mt-1 block w-full rounded-xl border border-gray-300 bg-white p-2.5 text-sm";
const HISTORY = ["emergency-history"];
type Attempt = { key: string; data: EmergencyDraft };

function savedAttempt(storageKey: string): Attempt | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey) || "null");
    if (typeof value?.key === "string" && typeof value.data?.building === "string" &&
        typeof value.data?.title === "string" && typeof value.data?.body === "string") return value;
  } catch { /* A missing or unavailable draft does not prevent composing. */ }
  return null;
}

export default function EmergencyNoticeForm() {
  const { user } = useAuth();
  return user ? <EmergencyComposer key={user.id} userId={user.id} /> : null;
}

function EmergencyComposer({ userId }: { userId: string }) {
  const { t } = useLanguage();
  const client = useQueryClient();
  const storageKey = `urbe360:emergency-draft:${userId}`;
  const [restored] = useState(() => savedAttempt(storageKey));
  const attempt = useRef<Attempt | null>(restored);
  const [building, setBuilding] = useState(restored?.data.building || "");
  const [title, setTitle] = useState(restored?.data.title || "");
  const [body, setBody] = useState(restored?.data.body || "");
  const [confirmed, setConfirmed] = useState(false);
  const length = title.length + 1 + body.length;
  const buildings = useQuery({ queryKey: ["buildings", "mine"], queryFn: listBuildings });
  const history = useQuery({ queryKey: HISTORY, queryFn: listEmergencies,
    refetchInterval: (query) => query.state.data?.some((row) => row.pending > 0) ? 2000 : 30_000 });
  const send = useMutation({ mutationFn: sendEmergency, onSuccess: (row) => {
    setConfirmed(false);
    setTitle("");
    setBody("");
    attempt.current = null;
    try { sessionStorage.removeItem(storageKey); } catch { /* The existing key remains safe to replay after a reload. */ }
    client.setQueryData<EmergencyOutcome[]>(HISTORY, (previous) => [row, ...(previous || []).filter((item) => item.id !== row.id)].slice(0, 20));
  }, onSettled: () => { void client.invalidateQueries({ queryKey: HISTORY }); } });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!confirmed || length > 1600 || send.isPending) return;
    const data = { building, title, body };
    if (!attempt.current || JSON.stringify(attempt.current.data) !== JSON.stringify(data)) {
      attempt.current = { key: crypto.randomUUID(), data };
    }
    // Keep the same operation across a timeout AND a page reload. A retry
    // recovers its receipt instead of asking the server for another broadcast.
    try { sessionStorage.setItem(storageKey, JSON.stringify(attempt.current)); } catch { /* The ref preserves in-tab retry safety. */ }
    send.mutate(attempt.current);
  }

  return <details className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
    <summary className="cursor-pointer text-sm font-semibold text-red-800"><AlertTriangle className="mr-2 inline h-4 w-4" />{t("m3.emergency.title")}</summary>
    <p className="mt-3 text-sm text-gray-600">{t("m3.emergency.description")}</p>
    <form onSubmit={submit} className="mt-4 space-y-3">
      <label className="block text-sm">{t("m3.documents.building")}<select name="building" required disabled={send.isPending} value={building} onChange={(event) => setBuilding(event.target.value)} className={FIELD}>
        <option value="" disabled>{t("m3.documents.chooseBuilding")}</option>
        {buildings.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label>
      <label className="block text-sm">{t("m3.documents.name")}<input name="title" required disabled={send.isPending} maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} className={FIELD} /></label>
      <label className="block text-sm">{t("m3.emergency.message")}<textarea name="body" required disabled={send.isPending} maxLength={Math.max(0, 1600 - title.length - 1)} value={body} onChange={(event) => setBody(event.target.value)} rows={4} className={FIELD} /></label>
      <p className={`text-xs ${length > 1600 ? "text-red-700" : "text-gray-500"}`}>{t("m3.emergency.limit", { count: length })}</p>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" disabled={send.isPending} checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-1" />{t("m3.emergency.confirm")}</label>
      {buildings.isError && <p role="alert" className="text-sm text-red-700">{t("m3.documents.buildingsError")}</p>}
      <button type="submit" disabled={!confirmed || length > 1600 || send.isPending || buildings.isPending || buildings.isError}
        className="rounded-xl bg-red-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{t(send.isPending ? "m3.emergency.sending" : "m3.emergency.send")}</button>
      {send.isError && <p role="alert" className="text-sm text-red-700">{t("m3.emergency.error")}</p>}
    </form>
    <h3 className="mt-6 text-sm font-semibold">{t("m3.emergency.history")}</h3>
    {history.isPending && <p role="status" className="mt-2 text-sm">{t("common.loading")}</p>}
    {history.isError && <p role="alert" className="mt-2 text-sm text-red-700">{t("m3.emergency.historyError")}</p>}
    <div className="mt-2 space-y-3">{history.data?.map((row) => <article key={row.id} className="rounded-xl border border-gray-200 p-3 text-sm" aria-label={row.title}>
      <p className="font-medium">{row.title} · {row.building_name}</p>
      <p className="mt-1 text-gray-600" role="status">{t(`m3.emergency.status.${row.status}`)}</p>
      <p className="mt-1 text-gray-600">{t("m3.emergency.outcome", {
        inApp: row.in_app, email: row.email_sent, emailFailed: row.email_failed, sms: row.sms_sent,
        failed: row.sms_failed, mocked: row.sms_mocked, pending: row.pending,
        skipped: row.email_skipped + row.sms_skipped, unknown: row.unknown,
      })}</p>
      {row.unknown > 0 && <p className="mt-2 text-amber-800">{t("m3.emergency.reviewUnknown")}</p>}
    </article>)}</div>
  </details>;
}
